import {
  type AgentContext,
  type ClientErrorEvent,
  FEEDBACK_CONTEXT_GROUPS,
  type FeedbackContext,
  type FeedbackContextGroup,
  type FeedbackDraftOmitGroup,
  type FeedbackType,
  reduceFeedbackContext,
  type SendFeedbackDraftRequest,
  type SendFeedbackRequest,
} from "@esi/shared";
import { currentRouteName } from "../router.tsx";
import { BUILD_ID } from "../shell/build-refresh.ts";
import { VERSION } from "../shell/release-notes.ts";
import { clientContext } from "../telemetry/environment.ts";
import { recentErrors } from "../telemetry/recent-errors.ts";

/**
 * The automatic context of a report (openspec change `in-app-feedback`, spec "Automatic context
 * is allowlisted"), reduced exactly as the API reduces it, so the dialog's preview shows each
 * value in the form it will be stored. Never a URL, an identifier or a title: the route is the
 * router's name for the page (`conversation`, `project.files`).
 */
export function currentFeedbackContext(
  input: { errors?: readonly ClientErrorEvent[]; route?: string; now?: Date } = {},
): FeedbackContext {
  const errors = input.errors ?? recentErrors();
  return reduceFeedbackContext(
    {
      app: { build: BUILD_ID, version: VERSION, time: (input.now ?? new Date()).toISOString() },
      route: input.route ?? currentRouteName(),
      device: clientContext(),
      ...(errors.length > 0 && { recentErrors: [...errors] }),
    },
    window.location.origin,
  );
}

/** The context groups the user left ticked; an unticked group is absent (never empty). */
export function pickContext(
  context: FeedbackContext,
  ticked: ReadonlySet<FeedbackContextGroup>,
): FeedbackContext {
  const out: FeedbackContext = {};
  for (const group of FEEDBACK_CONTEXT_GROUPS) {
    if (ticked.has(group) && context[group] !== undefined) {
      Object.assign(out, { [group]: context[group] });
    }
  }
  return out;
}

/**
 * The request body: built from the very object the preview shows, so what the user saw and
 * what is stored cannot differ (spec "The user sees exactly what is sent").
 */
export function feedbackRequest(
  type: FeedbackType,
  description: string,
  preview: FeedbackContext,
): SendFeedbackRequest {
  return { type, description: description.trim(), context: preview };
}

/**
 * A draft's send body (feedback-from-conversation D4), built from the previewed object like a
 * menu report's: the edited type and description, the ticked context, and every group the user
 * left out named in `omit` (the Assistant group too), so nothing unticked is sent.
 */
export function draftRequest(
  type: FeedbackType,
  description: string,
  preview: FeedbackContext,
  assistant: boolean,
): SendFeedbackDraftRequest {
  const omit: FeedbackDraftOmitGroup[] = FEEDBACK_CONTEXT_GROUPS.filter(
    (g) => preview[g] === undefined,
  );
  if (!assistant) {
    omit.push("assistant");
  }
  return {
    type,
    description: description.trim(),
    context: preview,
    ...(omit.length > 0 && { omit }),
  };
}

/**
 * An agent context as one line, every value as stored: the role, the tools, each failure with
 * its count, the outcome (`role chat · tools web_search · web_search: timeout ×2 · completed`).
 */
export function agentContextParts(context: AgentContext): string[] {
  const errors = new Map<string, number>();
  for (const e of context.errors) {
    const key = `${e.tool}: ${e.code}`;
    errors.set(key, (errors.get(key) ?? 0) + 1);
  }
  return [
    ...(context.role ? [`role ${context.role}`] : []),
    ...(context.tools.length > 0 ? [`tools ${context.tools.join(", ")}`] : []),
    ...[...errors].map(([key, n]) => (n > 1 ? `${key} ×${n}` : key)),
    context.outcome,
  ];
}
